/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  AvailableSchedule,
  FetchScheduleProperties,
  FilterScheduleInterFace,
  RequestScheduleInterFace,
  ScheduleInitialProperties,
} from '../../../interface/schedule-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import scheduleApi from '../../../utils/api/schedule';

// a thunk to fetch devices
export const fetchAllSchedules = createAsyncThunk<
  FetchScheduleProperties,
  RequestScheduleInterFace
>('schedule-management/fetchAllSchedules', async (data: RequestScheduleInterFace, { dispatch }) => {
  try {
    const response = await scheduleApi.fetchSchedules(removeEmptyKeys(data));
    return {
      ...response.data,
      results: response.data.results.map((deviceData: AvailableSchedule) => ({
        ...deviceData,
        startDateUtc: ConvertUTCtoDate(deviceData.startDateUtc),
        time: `${FormatUtcString(ConvertUTCtoHHMM(deviceData.startDateUtc))} - ${FormatUtcString(ConvertUTCtoHHMM(deviceData.endDateUtc))}`,
        user: deviceData.user,
        vehicleModel: deviceData.vehicleModel,
        id: deviceData._id,
      })),
    };
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'schedule-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await scheduleApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ScheduleInitialProperties = {
  schedules: [],
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  filter: {},
  totalResults: 0,
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const scheduleSlice = createSlice({
  name: 'schedule-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setScheduleFilterData: (
      state: ScheduleInitialProperties,
      action: PayloadAction<FilterScheduleInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetScheduleToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update station management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateScheduleFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<ScheduleInitialProperties>) => {
    builder
      .addCase(fetchAllSchedules.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAllSchedules.fulfilled,
        (state, action: PayloadAction<FetchScheduleProperties>) => {
          state.loading = false;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.schedules = action.payload.results;
        },
      )
      .addCase(fetchAllSchedules.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setScheduleFilterData,
  resetScheduleToInitialState,
  updateScheduleFilters,
  setSortData,
} = scheduleSlice.actions;
// Export the reducer
export default scheduleSlice.reducer;
